import type { Interpolation, Theme } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import BoostChat from 'bundles/common/components/BoostChat';
import { getActiveConversationId } from 'bundles/common/utils/chatLoaderUtils';
import useShouldShowHelpDropdown from 'bundles/composable-page-header/hooks/useShouldShowHelpDropdown';
import inServerContext from 'bundles/ssr/util/inServerContext';

import ErrorBoundaryWithTagging from './ErrorBoundaryWithTagging';

type Props = {
  /** Set isInlineButton to true when you are inserting a button into content that should not be floating on the bottom right and should always be shown */
  isInlineButton?: boolean;
  chatButtonBottomOffsetOverride?: number;
  floatingChatButtonContainerCssOverrides?: Interpolation<Theme>;
  /** When true, the Boost script and CSS overrides are still loaded but the floating ChatButton is hidden. Used when chat is triggered from a header dropdown instead. */
  hideFloatingButton?: boolean;
  boostInstanceName?: string;
  configValues?: Record<string, unknown>;
  skipTargetingCheck?: boolean;
};

/**
 * This component decides whether or not to show a chat button to talk to a chatbot or support agent.
 * If we ever want to do an A/B test such as bot vs human, or Boost chat vs. Salesforce chat, that logic should live here.
 */
const ChatLoader = ({
  isInlineButton = false,
  chatButtonBottomOffsetOverride,
  floatingChatButtonContainerCssOverrides,
  hideFloatingButton = false,
  boostInstanceName,
  configValues,
  skipTargetingCheck,
}: Props) => {
  const shouldShowHeaderHelpDropdown = useShouldShowHelpDropdown();
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  useEffect(() => {
    // Effect code is not run in SSR, which is what we want.  Setting the following using state (rather than a local variables) causes a 2nd render pass in CSR and avoids SSR<>CSR mismatch.
    setConversationId(getActiveConversationId());
  }, []);

  const showChatButton = !inServerContext && (skipTargetingCheck || isInlineButton || !!conversationId); // If there is an active chat conversation we always show the chat button on all pages because we want the learner to be able to easily resume that conversation.

  if (!showChatButton) {
    return null;
  }

  return (
    <div id="chat-loader">
      {/* We don't want any chance of the whole page crashing if something goes wrong with chat.  This error boundary logs and renders nothing in case of error. */}
      <ErrorBoundaryWithTagging componentName="common_chat_loader">
        <BoostChat
          conversationId={conversationId}
          isInlineButton={isInlineButton}
          chatButtonBottomOffsetOverride={chatButtonBottomOffsetOverride}
          floatingChatButtonContainerCssOverrides={floatingChatButtonContainerCssOverrides}
          hideFloatingButton={hideFloatingButton || shouldShowHeaderHelpDropdown}
          boostInstanceName={boostInstanceName}
          configValues={configValues}
        />
      </ErrorBoundaryWithTagging>
    </div>
  );
};

export default ChatLoader;
